import React from 'react';
import { Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Calendar, Eye, MousePointerClick, Percent, ArrowRight } from 'lucide-react';

import { processPercentageDisplay } from '../utils';
import {
  ctrFromAggregateData,
  formatNumber,
  formatWholeNumber,
} from '../../../../../core/utils/campaigns';
import { KpiCard } from '../../../../shared/designSystem/components/KpiBar';
import { formatDateOnly } from '../../../../shared/designSystem/utils/dateFormat';

// KpiCard + DeltaChip imported from the shared KpiBar module — previously
// defined locally (duplicated across 6 Campaign Details files). FlightDatesCard
// stays local: its body is a flight-specific layout (date pair + progress bar
// + % elapsed) too domain-specific to promote.

// =============================================================================
// FlightDatesCard — special variant: start → end dates + progress bar.
// Replaces the big-number slot on this column; everything else (icon, eyebrow)
// stays aligned with sibling KPI columns so the row reads as a unit.
// =============================================================================
const FlightDatesCard = ({ startDate, endDate, progressPercent }) => (
  <div className="campaign-overview__kpi">
    <div className="campaign-overview__kpi-head">
      <span className="campaign-overview__kpi-icon campaign-overview__kpi-icon--neutral">
        <Calendar size={16} />
      </span>
      <Tooltip
        overlayClassName="mjl-ds"
        title="Flight Dates refers to the Start & End Dates of the individual campaign"
      >
        <span className="campaign-overview__kpi-eyebrow">Flight Dates</span>
      </Tooltip>
    </div>
    <div className="campaign-overview__flight">
      <div className="campaign-overview__flight-dates">
        {/* UTC-anchored: the flight dates are stored UTC-midnight, and the chart
            + picker read them in UTC. Rendering via plain local-tz formatDateOnly
            pushed a `…T23:59:59.999Z` end into the next local day (e.g. Aug 1 for
            a PH viewer of a July 31 end). Normalize to the UTC day-string first so
            the card matches the picker/chart. */}
        <span>{formatDateOnly(startDate ? moment.utc(startDate).format('YYYY-MM-DD') : null, '—')}</span>
        <ArrowRight size={14} className="campaign-overview__flight-arrow" />
        <span>{formatDateOnly(endDate ? moment.utc(endDate).format('YYYY-MM-DD') : null, '—')}</span>
      </div>
      <div className="campaign-overview__flight-progress">
        <div
          className="campaign-overview__flight-progress-bar"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="campaign-overview__flight-meta">
        {progressPercent}% elapsed
      </div>
    </div>
  </div>
);

const CampaignDetailBar = ({
  selectedCampaign,
  byCampaignRangeDate,
  dataCampaignRangeDate,
  providerIndex,
  providersTab,
}) => {
  const { startDate, endDate, aggregateData } = selectedCampaign;

  dataCampaignRangeDate = dataCampaignRangeDate.map((data) => {
    return {
      ...data,
      xAxis: moment(data.xAxis).utc().format('YYYY-MM-DD'),
    };
  });

  const getProviderIndex = providerIndex !== 'All' ? providerIndex : 0;

  let kpiPercentage = {
    percentages: { impressions: 0, clicks: 0, ctr: 0 },
    previousValues: { impressions: 0, clicks: 0, ctr: 0 },
    ranges: '',
  };

  const clicks =
    providersTab &&
    !isEmpty(providersTab[parseInt(getProviderIndex)]) &&
    !isEmpty(providersTab[parseInt(getProviderIndex)][1]) &&
    providersTab[parseInt(getProviderIndex)][1].overallData.clicks;

  const impressions =
    providersTab &&
    !isEmpty(providersTab[parseInt(getProviderIndex)]) &&
    !isEmpty(providersTab[parseInt(getProviderIndex)][1]) &&
    providersTab[parseInt(getProviderIndex)][1].overallData.impressions;

  const getSelectedStartDate =
    byCampaignRangeDate && byCampaignRangeDate.length > 0
      ? moment(byCampaignRangeDate[0]).format('MM/DD/YYYY')
      : null;
  const getSelectedEndDate =
    byCampaignRangeDate && byCampaignRangeDate.length > 0
      ? moment(byCampaignRangeDate[1]).format('MM/DD/YYYY')
      : null;

  if (!isEmpty(providersTab) && dataCampaignRangeDate) {
    kpiPercentage = processPercentageDisplay(
      [getSelectedStartDate, getSelectedEndDate],
      dataCampaignRangeDate,
      providersTab[parseInt(getProviderIndex)][1].byDate
    );
  }

  const getImpressions = (item) => item.impressions;
  const getClicks = (item) => item.clicks;
  const sumTotal = (prev, next) => prev + next;

  const getTotalImpressions = isEmpty(dataCampaignRangeDate)
    ? null
    : dataCampaignRangeDate.map(getImpressions).reduce(sumTotal);
  const getTotalClicks = isEmpty(dataCampaignRangeDate)
    ? null
    : dataCampaignRangeDate.map(getClicks).reduce(sumTotal);

  const ctrFromSelectedDate = () => {
    let ctr = 0;
    let c = 0;
    let i = 0;
    if (getTotalClicks && getTotalImpressions) {
      c = c + getTotalClicks;
      i = i + getTotalImpressions;
    }
    if (c && i) {
      ctr = c / i;
    }
    return ctr * 100;
  };

  const getProgress = () => {
    const total = () => {
      const a = moment(endDate);
      const b = moment(startDate);
      return a.diff(b, 'days');
    };
    const progress = () => {
      const a = moment();
      const b = moment(startDate);
      return a.diff(b, 'days');
    };
    const totaldays = total();
    const progressDays = progress();
    if (progressDays > totaldays || progressDays === totaldays) return 100;
    const daysleft = totaldays - progressDays;
    const daysin = totaldays - daysleft;
    return Math.floor((daysin / totaldays) * 100);
  };

  const currentposition = getProgress();

  const impressionCount =
    providerIndex === 'All'
      ? aggregateData
        ? isEmpty(dataCampaignRangeDate)
          ? formatWholeNumber(aggregateData.overallData.aggImpressions)
          : formatWholeNumber(getTotalImpressions)
        : 'N/A'
      : !isEmpty(dataCampaignRangeDate)
        ? formatWholeNumber(getTotalImpressions)
        : formatWholeNumber(impressions);

  const clicksCount =
    providerIndex === 'All'
      ? aggregateData
        ? isEmpty(dataCampaignRangeDate)
          ? formatWholeNumber(aggregateData.overallData.aggClicks)
          : formatWholeNumber(getTotalClicks)
        : 'N/A'
      : !isEmpty(dataCampaignRangeDate)
        ? formatWholeNumber(getTotalClicks)
        : formatWholeNumber(clicks);

  const ctrDisplay = aggregateData
    ? isEmpty(byCampaignRangeDate)
      ? `${formatNumber(ctrFromAggregateData(aggregateData))}`
      : `${formatNumber(ctrFromSelectedDate())}`
    : 'N/A';

  const { percentages, previousValues, ranges } = kpiPercentage;

  // Renders only the KPI bar — the unified performance card wrapper
  // (section + header + range selector) lives in CampaignsDetailsView so
  // that KPI bar, range selector, and chart all share one card.
  return (
    <div className="campaign-overview__kpi-bar">
      <FlightDatesCard
        startDate={startDate}
        endDate={endDate}
        progressPercent={currentposition}
      />

      <KpiCard
        icon={<Eye size={16} />}
        iconTint="primary"
        label="Impressions"
        value={impressionCount}
        delta={{
          value: percentages.impressions,
          prev: formatWholeNumber(previousValues.impressions),
          ranges,
        }}
      />

      <KpiCard
        icon={<MousePointerClick size={16} />}
        iconTint="emerald"
        label="Clicks"
        value={clicksCount}
        delta={{
          value: percentages.clicks,
          prev: formatWholeNumber(previousValues.clicks),
          ranges,
        }}
      />

      <KpiCard
        icon={<Percent size={16} />}
        iconTint="purple"
        label="CTR"
        value={ctrDisplay}
        valueSuffix="%"
        delta={{
          value: percentages.ctr,
          prev: `${formatNumber(previousValues.ctr)}%`,
          ranges,
        }}
      />
    </div>
  );
};

export default CampaignDetailBar;
